import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { ChartCard, EmptyChart } from '../charts/ChartPrimitives';
import { CHART, CATEGORICAL_FILLS, formatUnits } from '../../lib/reporting-format';

type ByLocation = { locationId: string; locationName: string; value: number; units: number };
type ByProductType = { productType: string; value: number; units: number };
type ByCategory = { category: string; value: number; units: number };

interface BreakdownsProps {
  loading: boolean;
  byLocation: ByLocation[];
  byProductType: ByProductType[];
  byCategory: ByCategory[];
}

/**
 * The three Stock Value cuts: by location (sorted bar), by product type POD/PAP
 * (donut — the key cut), and by category (sorted bar). All valued at WAC.
 */
export const StockValueBreakdowns: React.FC<BreakdownsProps> = ({
  loading,
  byLocation,
  byProductType,
  byCategory,
}) => {
  const { t } = useTranslation('inventory');
  const { format, currency } = useCurrencyFormatter();

  const locationData = React.useMemo(
    () => [...byLocation].sort((a, b) => b.value - a.value),
    [byLocation],
  );
  const categoryData = React.useMemo(
    () => [...byCategory].sort((a, b) => b.value - a.value).slice(0, 12),
    [byCategory],
  );
  const typeData = React.useMemo(
    () => [...byProductType].sort((a, b) => b.value - a.value),
    [byProductType],
  );

  const money = (v: number) => format(v, currency);

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* By location */}
      <ChartCard title={t('reporting.v2.byLocation', 'By location')}>
        {loading ? (
          <div className="h-64 animate-pulse rounded bg-muted/40" />
        ) : locationData.length === 0 ? (
          <EmptyChart message={t('reporting.v2.noLocationValue', 'No stock value by location.')} className="h-64" />
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={locationData} layout="vertical" margin={{ top: 4, right: 12, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: CHART.axis }}
                  tickLine={false}
                  axisLine={{ stroke: CHART.grid }}
                  tickFormatter={money}
                />
                <YAxis
                  type="category"
                  dataKey="locationName"
                  tick={{ fontSize: 11, fill: CHART.axis }}
                  tickLine={false}
                  axisLine={false}
                  width={90}
                />
                <Tooltip
                  formatter={(v: number, _n, item) => [
                    `${money(v)} · ${formatUnits((item?.payload as ByLocation)?.units)} units`,
                    '',
                  ]}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Bar dataKey="value" fill={CHART.purple} radius={[0, 4, 4, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartCard>

      {/* By product type — the key POD/PAP cut */}
      <ChartCard
        title={t('reporting.v2.byProductType', 'By product type')}
        description={t('reporting.v2.podPapCut', 'POD vs PAP, the key split.')}
      >
        {loading ? (
          <div className="h-64 animate-pulse rounded bg-muted/40" />
        ) : typeData.length === 0 ? (
          <EmptyChart message={t('reporting.v2.noTypeValue', 'No stock value by type.')} className="h-64" />
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={typeData}
                  dataKey="value"
                  nameKey="productType"
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                >
                  {typeData.map((entry, i) => (
                    <Cell
                      key={entry.productType}
                      fill={CATEGORICAL_FILLS[i % CATEGORICAL_FILLS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(v: number, _n, item) => [
                    `${money(v)} · ${formatUnits((item?.payload as ByProductType)?.units)} units`,
                    (item?.payload as ByProductType)?.productType ?? '',
                  ]}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartCard>

      {/* By category */}
      <ChartCard title={t('reporting.v2.byCategory', 'By category')}>
        {loading ? (
          <div className="h-64 animate-pulse rounded bg-muted/40" />
        ) : categoryData.length === 0 ? (
          <EmptyChart message={t('reporting.v2.noCategoryValue', 'No stock value by category.')} className="h-64" />
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryData} layout="vertical" margin={{ top: 4, right: 12, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={CHART.grid} horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: CHART.axis }}
                  tickLine={false}
                  axisLine={{ stroke: CHART.grid }}
                  tickFormatter={money}
                />
                <YAxis
                  type="category"
                  dataKey="category"
                  tick={{ fontSize: 11, fill: CHART.axis }}
                  tickLine={false}
                  axisLine={false}
                  width={90}
                />
                <Tooltip
                  formatter={(v: number, _n, item) => [
                    `${money(v)} · ${formatUnits((item?.payload as ByCategory)?.units)} units`,
                    '',
                  ]}
                  contentStyle={{ fontSize: 12, borderRadius: 8 }}
                />
                <Bar dataKey="value" fill={CHART.slateDark} radius={[0, 4, 4, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </ChartCard>
    </div>
  );
};
